/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import useEmblaCarousel from "embla-carousel-react";
import React, { FC, ReactNode } from "react";

import { css, cva, cx } from "@panda/css";
import { VStack } from "@panda/jsx";

import type { RecipeVariantProps } from "@panda/css";
import type { EmblaOptionsType } from "embla-carousel";
import {
	CosmosCarouselDotButton,
	CosmosCarouselPrevNextButtons,
	CosmosCarouselPrevNextButtonsProps,
	useCosmosCarouselDotButtons,
	useCosmosCarouselPrevNextButtons,
} from "./CosmosCarouselButtons";
import { CosmosCarouselThumbnails } from "./CosmosCarouselThumbnails";
import { Caption } from "../caption";

const carouselViewportWrapper = css({
	"--page-inline-padding": "token(spacing.eds.dimension.24)",
	display: "flex",
	flexDirection: "column",
	maxWidth: "calc(100vw - var(--page-inline-padding) * 2)",
	paddingInline: "var(--page-inline-padding)",
	marginInline: "calc(-1 * var(--slide-size))",
	overflow: "hidden",
});

const carouselViewport = css({
	position: "relative",
	overflow: "visible",
	maxWidth: "min(100%, 1920px)",
	marginInline: "auto",
});

const carouselItems = css({
	display: "flex",
	position: "relative",
});

const carouselItem = cva({
	base: {
		minWidth: 0,
		overflow: "hidden",
		position: "relative",
		transform: "translate3d(0, 0, 0)",
		flex: "0 0 min(100%, var(--slide-size))",
		paddingInline: "calc(var(--slide-spacing) / 2)",
	},
	variants: {
		aspectRatio: {
			"16/9": { aspectRatio: "16/9" },
			"4/3": { aspectRatio: "4/3" },
			"1/1": { aspectRatio: "1/1" },
		},
	},
});

const carouselItemScaleWrapper = cva({
	base: {
		transform: "scale(1)",
		width: "100%",
		height: "100%",
		transition: "all 0.5s ease",
	},
	variants: {
		type: {
			selected: {},
			normal: {},
			scaled: {
				transform: "scale(var(--scaled-ratio))",
			},
		},
	},
});

const carouselDotsStyles = css({
	display: "flex",
	position: "relative",
	justifyContent: "center",
	alignItems: "center",
	flexDirection: "row",
	gap: "eds.dimension.8",
	width: "auto",
	marginInline: "auto",
});

export type CarouselItemsVariants = RecipeVariantProps<typeof carouselItem>;

export type CosmosCarouselProps = {
	aspectRatio?: "16/9" | "4/3" | "1/1";
	bleed?: string;
	buttonPosition?: "inside" | "outside";
	maxWidth?: string;
	loop?: EmblaOptionsType["loop"];
	slides: ReactNode[];
	slidesInView?: number;
	caption?: string | null;
	thumbnails?: boolean;
	dots?: boolean;
	className?: string;
	slideSpacing?: string;
	slideSize?: string;
	scaledRatio?: string;
	carouselType?: "normal" | "scaled";
	buttons?: boolean;
	buttonSize?: CosmosCarouselPrevNextButtonsProps["buttonSize"];
};

export const CosmosCarousel: FC<CosmosCarouselProps> = ({
	aspectRatio,
	buttonPosition = "outside",
	loop = true,
	slides,
	slidesInView = 1,
	caption,
	thumbnails = false,
	dots = false,
	slideSpacing = "1rem",
	slideSize = "1200px",
	scaledRatio = "0.7",
	className,
	carouselType = "normal",
	buttons = true,
	buttonSize = "default",
}): React.JSX.Element | null => {
	const [emblaRef, emblaApi] = useEmblaCarousel({
		loop,
		skipSnaps: true,
		slidesToScroll: 1,
	});

	const { scrollPrev, scrollNext, prevBtnDisabled, nextBtnDisabled } = useCosmosCarouselPrevNextButtons(emblaApi);

	const { selectedIndex, scrollSnaps, onDotButtonClick } = useCosmosCarouselDotButtons(emblaApi);

	if (!slides?.length) return null;

	return (
		<VStack
			className={cx(
				"cosmos-carousel",
				css({
					"--slide-spacing": slideSpacing,
					"--slide-size": slideSize,
					"--scaled-ratio": scaledRatio,
				}),
				className,
			)}
			gap="eds.dimension.56"
			position="relative"
		>
			<VStack
				className={cx("cosmos-carousel-viewport", carouselViewportWrapper)}
				gap="eds.dimension.16"
				position="relative"
			>
				<Caption caption={caption}>
					<div className={carouselViewport} ref={emblaRef}>
						<div className={carouselItems}>
							{slides.map((slide, index) => (
								<div className={carouselItem({ aspectRatio })} key={index}>
									<div
										className={carouselItemScaleWrapper({
											type: selectedIndex === index ? "selected" : carouselType,
										})}
									>
										{slide}
									</div>
								</div>
							))}
						</div>
					</div>
				</Caption>

				{buttons ? (
					<CosmosCarouselPrevNextButtons
						buttonPosition={buttonPosition}
						scrollPrev={scrollPrev}
						scrollNext={scrollNext}
						prevBtnDisabled={prevBtnDisabled}
						nextBtnDisabled={nextBtnDisabled}
						buttonSize={buttonSize}
					/>
				) : null}
			</VStack>
			{dots && slidesInView === 1 ? (
				<div className={cx("cosmos-carousel-dots", carouselDotsStyles)}>
					{scrollSnaps.map((_, index) => (
						<CosmosCarouselDotButton
							key={index}
							onClick={() => onDotButtonClick(index)}
							isSelected={selectedIndex === index}
							aria-label={`Slide ${index + 1}`}
						/>
					))}
				</div>
			) : null}
			{thumbnails && slidesInView === 1 ? <CosmosCarouselThumbnails emblaApi={emblaApi} slides={slides} /> : null}
		</VStack>
	);
};
